// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file Collapsible.tsx
 * @input Uses React, StyleX, useCollapsible hook, CollapsibleGroupPresentationContext, getIcon, theme tokens
 * @output Exports Collapsible component and CollapsibleProps
 * @position Collapsible content primitive — trigger toggles visibility of children
 *
 * Collapsible is a standalone primitive that makes any content collapsible.
 * It renders a trigger area (always visible) and a content area that toggles.
 * Handles state management, accessibility (aria-expanded + aria-controls linking
 * the trigger to its content region), and chevron indicator.
 *
 * Works standalone or coordinated by CollapsibleGroup via the `value` prop.
 * When the surrounding CollapsibleGroup sets `hasDividers`, each Collapsible
 * draws its own row chrome (borderBlockStart suppressed on :first-child, plus
 * density padding) from CollapsibleGroupPresentationContext — StyleX has no
 * child selectors, so the group cannot draw it from outside. The presentation
 * context is reset around children so nested collapsibles stay chrome-free.
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/Collapsible/index.ts (exports)
 * - /packages/core/src/Collapsible/Collapsible.doc.mjs
 * - /apps/storybook/stories/Collapsible.stories.tsx
 * - /packages/cli/assets/templates/blocks/components/Collapsible/ (showcase blocks)
 */
import { use, useId } from 'react';
import * as stylex from '@stylexjs/stylex';
import "../theme/tokens.stylex.js";
import { borderVars, colorVars, typographyVars, fontWeightVars, spacingVars, typeScaleVars, durationVars, easeVars } from "../theme/tokens.stylex.js";
import { useCollapsible } from "./useCollapsible.js";
import { CollapsibleGroupPresentationContext } from "./CollapsibleGroupContext.js";
import { Icon } from "../Icon/index.js";
import { mergeProps } from "../utils/index.js";
import { themeProps } from "../utils/themeProps.js";
import { focusOutlineProps } from "../utils/focusOutline.stylex.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const styles = {
  root: {
    kzqmXN: "xh8yej3",
    $$css: true
  },
  trigger: {
    kB7OPa: "x9f619",
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kjj79g: "x1qughib",
    kzqmXN: "xh8yej3",
    kkrTdU: "x1ypdohk",
    kMv6JI: "x9ynric",
    kGuDYH: "x18juvz8",
    k63SB2: "x2mo6ok",
    kMwMTN: "x1tgivj0",
    k9WMMc: "x1yc453h",
    k8WAf4: "xt970qd",
    $$css: true
  },
  triggerDisabled: {
    kkrTdU: "x1h6gzvc",
    kSiTet: "xbyyjgo",
    $$css: true
  },
  chevron: {
    k1xSpc: "x3nfvp2",
    kGNEyG: "x6s0dn4",
    kjj79g: "xl56j7k",
    kmuXW: "x2lah0s",
    kzqmXN: "xta46cu",
    kZKoxP: "x1seg80s",
    kGuDYH: "x18juvz8",
    k1ekBW: "x11xpdln",
    kIyJzY: "xuedmi6",
    kAMwcw: "xlr8y92",
    $$css: true
  },
  chevronOpen: {
    k3aq6I: "x19jd1h0",
    $$css: true
  },
  chevronClosed: {
    k3aq6I: "x7p49u4",
    $$css: true
  },
  contentHidden: {
    k1xSpc: "x1s85apg",
    $$css: true
  },
  content: {
    kLKAdn: "xfsso4q",
    kMv6JI: "x9ynric",
    kGuDYH: "xjm74w1",
    k63SB2: "xxovm9e",
    kLWn49: "xw6l6zx",
    kMwMTN: "x1tgivj0",
    $$css: true
  },
  divided: {
    kEafiO: "x11xkdxz x1g31smg",
    kPef9Z: "x13fuv20",
    kLZC3w: "x1pc3f07",
    $$css: true
  }
};

// Density padding for divided/padded accordion rows. paddingBlock mapping
// follows Table's density scale (spacing-1/2/3); content only pads its end
// so text doesn't sit on the divider below (block-start stays spacing-1).
const densityStyles = {
  triggerCompact: {
    k8WAf4: "xu0wf1k",
    $$css: true
  },
  triggerBalanced: {
    k8WAf4: "xce4md1",
    $$css: true
  },
  triggerSpacious: {
    k8WAf4: "x8o8v82",
    $$css: true
  },
  contentCompact: {
    kGO01o: "xy143xn",
    $$css: true
  },
  contentBalanced: {
    kGO01o: "x1wesfrj",
    $$css: true
  },
  contentSpacious: {
    kGO01o: "xvmdzux",
    $$css: true
  }
};
const triggerDensity = {
  compact: densityStyles.triggerCompact,
  balanced: densityStyles.triggerBalanced,
  spacious: densityStyles.triggerSpacious
};
const contentDensity = {
  compact: densityStyles.contentCompact,
  balanced: densityStyles.contentBalanced,
  spacious: densityStyles.contentSpacious
};
/**
 * A primitive that makes any content collapsible.
 *
 * Renders a trigger area (always visible) with a chevron indicator,
 * and a content area that toggles visibility on click.
 * Handles its own state by default, or defers to CollapsibleGroup
 * when a `value` prop is provided and a group is present.
 *
 * Use inside Card for elevated collapsible sections.
 * Wrap multiple instances in CollapsibleGroup for accordion behavior.
 *
 * @example
 * ```
 * <Collapsible trigger="Details">
 *   <Text type="body">Collapsible content</Text>
 * </Collapsible>
 * <Card>
 *   <Collapsible trigger="Settings">
 *     <SettingsForm />
 *   </Collapsible>
 * </Card>
 * <CollapsibleGroup type="single" defaultValue="general">
 *   <VStack gap={2}>
 *     <Card>
 *       <Collapsible trigger="General" value="general">
 *         <GeneralSettings />
 *       </Collapsible>
 *     </Card>
 *     <Card>
 *       <Collapsible trigger="Advanced" value="advanced">
 *         <AdvancedSettings />
 *       </Collapsible>
 *     </Card>
 *   </VStack>
 * </CollapsibleGroup>
 * ```
 */
export function Collapsible({
  trigger,
  children,
  defaultIsOpen,
  isOpen: controlledIsOpen,
  isDisabled = false,
  onOpenChange,
  value,
  ref,
  xstyle,
  className,
  style,
  ...props
}) {
  // Build the config for the hook
  const collapsibleConfig = controlledIsOpen !== undefined ? {
    isOpen: controlledIsOpen,
    onOpenChange
  } : {
    defaultIsOpen: defaultIsOpen ?? true,
    onOpenChange
  };
  const {
    isOpen,
    toggle
  } = useCollapsible({
    isCollapsible: collapsibleConfig,
    value
  });

  // Activation is blocked by this guard rather than the native `disabled`
  // attribute, so the trigger keeps `aria-disabled` semantics and stays
  // discoverable. A native `disabled` button would silently swallow events
  // (e.g. a wrapping tooltip's hover) — the system-wide disabled convention.
  const handleToggle = () => {
    if (isDisabled) {
      return;
    }
    toggle();
  };
  const presentation = use(CollapsibleGroupPresentationContext);
  const isDivided = presentation?.hasDividers ?? false;
  const density = presentation?.density ?? null;

  // Links the trigger to the region it shows/hides so assistive tech can move
  // from the button to its controlled content (disclosure pattern).
  const contentId = useId();
  return /*#__PURE__*/_jsxs("div", {
    ref: ref,
    ...mergeProps(themeProps('collapsible', {
      density: density ?? undefined
    }), stylex.props(styles.root, isDivided && styles.divided, xstyle), className, style),
    ...props,
    children: [/*#__PURE__*/_jsxs("button", {
      type: "button",
      onClick: handleToggle,
      "aria-disabled": isDisabled || undefined,
      "aria-expanded": isOpen,
      "aria-controls": contentId
      // A disabled trigger drops out of the tab order so it isn't a silently
      // dead tab stop; activation stays blocked by the handleToggle guard,
      // and aria-disabled keeps the state perceivable to assistive tech —
      // the system-wide disabled convention (never native `disabled`, which
      // would swallow events like a wrapping tooltip's hover).
      ,
      tabIndex: isDisabled ? -1 : undefined,
      ...mergeProps(themeProps('collapsible-trigger', {
        density: density ?? undefined
      }), focusOutlineProps.focusVisible(styles.trigger, density != null && triggerDensity[density], isDisabled && styles.triggerDisabled)),
      children: [/*#__PURE__*/_jsx("span", {
        ...{
          className: "x1b2iylo xwgcxoh"
        },
        children: trigger
      }), /*#__PURE__*/_jsx(Icon, {
        icon: "chevronDown"
        // Nearest size to the trigger's 17px type step; `chevron` re-pins the
        // exact box (see the style) so the glyph does not resize.
        ,
        size: "sm"
        // Was `--color-icon-secondary` on the old wrapper span; `secondary`
        // is the same token, expressed as an Icon color.
        ,
        color: "secondary",
        xstyle: [styles.chevron, isOpen ? styles.chevronOpen : styles.chevronClosed]
      })]
    }), /*#__PURE__*/_jsx("div", {
      id: contentId,
      ...mergeProps(themeProps('collapsible-content', {
        density: density ?? undefined
      }), stylex.props(styles.content, density != null && contentDensity[density], !isOpen && styles.contentHidden)),
      children: presentation != null ? /*#__PURE__*/_jsx(CollapsibleGroupPresentationContext, {
        value: null,
        children: children
      }) : children
    })]
  });
}
Collapsible.displayName = 'Collapsible';